import React, { useState, useEffect, useCallback, useRef } from "react";
import { throttle } from "lodash";
import styled from "@emotion/styled";
import Theme from '../../../themes/index';
import PropTypes from "prop-types";
import _ from "lodash";

// Components - Inputs
import Label from "../input-elements/oc-label";
import TextInput from "../input-elements/oc-text-input";
import EmailInput from "../input-elements/oc-email-input";
import PhoneInput from "../input-elements/oc-phone-input";
import NumberInput from "../input-elements/oc-numeric-input";
import Checkbox from "../input-elements/oc-checkbox";
import RadioButton from "../input-elements/oc-radio-button";
import ImageRadioButton from "../input-elements/oc-image-radio-button";
import SelectDropdown from "../input-elements/oc-select-dropdown";
import FormButton from "../input-elements/oc-form-button";
import InputError from "../input-elements/oc-input-error";

// Components - Groups / Wrappers
import FormProgress from "./oc-form-progress";
import FormPage from "./oc-form-page";
import Fieldset from "../input-elements/oc-fieldset";
import FormGroup from "./oc-form-group";
import InputList from "../input-elements/oc-input-list";
import RadioGroup from "../input-elements/oc-radio-group";
import CheckboxGroup from "../input-elements/oc-checkbox-group";
import FormModal from "./oc-form-modal";

// Components - Frequent Input Groupings
import FormName from "./oc-form-name";
import FormAddress from "./oc-form-address";
import FormContact from "./oc-form-contact";
import FormError from "./oc-form-error";

// Form Component Map
const FieldMap = {
  text: TextInput,
  email: EmailInput,
  phone: PhoneInput,
  textarea: TextInput,
  number: NumberInput,
  checkbox: CheckboxGroup,
  radio: RadioGroup,
  imageRadio: RadioGroup,
  select: SelectDropdown
};

// Styled Components
const Form = styled.form`
  background-color: #F8F8F8;
  display: inline-block;
  padding: 10px;
  position: relative;
  width: 100%;

  @media screen and (min-width: 768px) {
    padding: 40px;
    width: ${props => props.showStartPage ? '62%' : '100%'};
  }
`;

const FormFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;

  .buttons-container {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 16px;
    position: relative;

    button {
      margin-right: 1em;
    }
  }

  .form-branding {
    align-items: center;
    display: flex;
    justify-content: flex-end;

    p {
      font-size: 12px;
      max-width: 120px;
      margin-right: 8px;
      overflow-wrap: break-word;
      text-align: right;
    }

    img {
      width: 33px;
    }
  }
`;

const Disclaimers = styled.div`
  display:inline-block;
  position: relative;
  width: 100%;
`;

const Note = styled.p`
  color: #585858;
  font-family: 'OCRoboto';
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
  margin-bottom: 8px;
`;

const FormWrapper = ({
  action,
  children,
  method = "POST",
  pages,
  themeColor = Theme.colors.brand,
  showStartPage,
  successComponent,
  successComponentProps = {},
  uid,
  ...props
}) => {
  const formRef = useRef()
  const formProps = props;
  const pageCount = pages?.length;
  const [currentPage, setCurrentPage] = useState(0);
  const [isBlockedForSubmission, toggleBlockedForSubmission] = useState(false);
  const [pageData, setPageData] = useState(
    Array.from({ length: pageCount }, () => ({ values: {}, touched: {}, errors: {} }))
  );
  const [formModalContent, setFormModalContent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const [submissionErrorChildren, setSubmissionErrorChildren] = useState(null);
  const [isPageInitialized, setPageInitialized] = useState(false);
  const lastPage = currentPage === (pageCount - 1);
  const defaultNextText = lastPage ? "Submit" : "Next";
  const nextText = pages[currentPage]?.nextText || defaultNextText;
  const disclaimers = pages[currentPage]?.disclaimers || [];

  // TODO: QW-specific? Migrate form-specific values and functions
  // out of wrapper and pass into form via props
  const [estimateValues, setEstimateValues] = useState(null);
  const [formData, setFormData] = useState(null);

  const openFormModal = (content) => {
    const Component = content;
    setFormModalContent(<Component />);
  }

  const closeFormModal = () => {
    setFormModalContent(null);
  }

  // ------------------------------------------------------------------------------------------

  // Page value initialization
  const initializePageData = (pages) => {
    if (isPageInitialized)
      return;

    pages.forEach((page, pageIndex) => {
      let newPageData = { ...pageData[pageIndex] };
      page.fields.filter(field => {
        return field.value && !validationErrors(field, field.name, field.value.toString().trim())
      }).forEach(field => newPageData["values"][field.name] = field.value)
      if (Object.keys(newPageData["values"]).length > 0) {
        updatePageData(pageIndex, newPageData);
      }
    });

    setPageInitialized(true);
  };

  // Validation functions
  const updatePageData = (pageIndex, data) => {
    setPageData(existingData => {
      return [
        ...existingData.slice(0, pageIndex),
        existingData[pageIndex] = data,
        ...existingData.slice(pageIndex + 1)
      ]
    });
  }

  const removeDependentValues = (fieldName, values) => {
    let conditionalFields = pages[currentPage].fields.filter((field) => field.conditional);
    let dependentFields = conditionalFields.filter((field) => field.conditional?.input == fieldName);
    let newValue = values[fieldName];

    // If a value with a conditional field dependency is removed,
    // also remove the dependent field's value.
    dependentFields.forEach((field) => {
      if (field.conditional.anyValue) {
        if (!newValue?.length) delete values[field.name];
      } else {
        if (Array.isArray(newValue)) {
          if (!newValue.includes(field.conditional?.value)) delete values[field.name];
        } else {
          if (newValue != field.conditional?.value) delete values[field.name];
        }
      }
    });
  }

  const updateValues = (event, field, name = field.name, value = event.target.value, pageIndex = currentPage) => {
    let newPageData = { ...pageData[pageIndex] };
    let newValues = { ...pageData[pageIndex].values };
    let newValue = value;

    newValues[name] = newValue;
    removeDependentValues(name, newValues);

    newPageData["values"] = newValues;
    validateField(newPageData, field, name, newValue, pageIndex);
  }

  const updateTouched = (event, field) => {
    let newPageData = { ...pageData[currentPage] };
    let newTouched = { ...pageData[currentPage].touched };
    let key = field.name;
    let value = event.target.value;

    newTouched[key] = true;
    newPageData["touched"] = newTouched;
    validateField(newPageData, field, key, value);
  }

  const validEmail = (value) => {
    return /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i.test(value);
  }

  const validPhone = (value) => {
    return /^(\([0-9]{3}\)) [0-9]{3}-[0-9]{4}$/.test(value);
  }

  const conditionalFieldIsVisible = (pageIndex, field) => {
    const conditionedValue = pageData[pageIndex].values[field.conditional.input];

    // NOTE: So far we're specifically checking string and array values
    // for fields that are dependent on a specific selection
    if (field.conditional.anyValue) {
      return !!conditionedValue;
    } else {
      if (Array.isArray(conditionedValue)) {
        return conditionedValue.includes(field.conditional.value);
      } else {
        return field.conditional.value == conditionedValue;
      }
    }
  };

  const validateField = (newData, field, name, value, pageIndex = currentPage) => {
    let newPageData = newData;
    let newErrors = { ...pageData[pageIndex].errors };
    let formattedValue = (value)?.toString().trim();
    let errorMessage = validationErrors(field, name, formattedValue);

    if (field.conditional && !conditionalFieldIsVisible(pageIndex, field)) {
      delete newErrors[name];
    } else {
     if (errorMessage) {
        newErrors[name] = errorMessage;
      } else {
        delete newErrors[name];
      }
    }

    newPageData["errors"] = newErrors;
    updatePageData(pageIndex, newPageData);
  }

  const validationErrors = (field, name, value) => {
    let requiredError = field.requiredError || "Please provide a value";

    if (field.required && !value) {
      return requiredError;
    } else if ((name == "email" || name == 'secondary-email') && value && !validEmail(value)) {
      return "Please enter a valid email address";
    } else if (name == "phone" && value && !validPhone(value)) {
      return "Please use the following format: (123) 456-7890";
    } else {
      return false;
    }
  }

  // ------------------------------------------------------------------------------------------

  // Progression & submission functions
  const prevPage = (event) => {
    event.preventDefault();
    event.target.blur();

    if (pages[currentPage] && pages[currentPage].onPrevious) {
      pages[currentPage].onPrevious({ trigger: "prevButton" });
    }

    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }

  const nextOrSubmit = (event, args = {}) => {
    event.preventDefault();
    event.target.blur();

    let currentPageIsValid = false;
    let currentPageData = { ...pageData[currentPage] };
    let newErrors = { ...currentPageData.errors };

    pages[currentPage]?.fields.map((field) => {
      if (field.conditional && !conditionalFieldIsVisible(currentPage, field)) {
        delete newErrors[field.name];
        return;
      }

      if (field.customValidation) {
        let validationObject = field.customValidation(currentPageData["values"][field.name]);
        let valid = validationObject.valid;
        let error = validationObject.error;

        if (valid) {
          delete newErrors[field.name];
        } else {
          newErrors[field.name] = error;
        }
      } else {
        let formattedValue = (currentPageData["values"][field.name])?.toString().trim();
        let errorMessage = validationErrors(field, field.name, formattedValue);

        if (errorMessage) {
          newErrors[field.name] = errorMessage;
        } else {
          delete newErrors[field.name];
        }
      }
    });

    currentPageData["errors"] = newErrors;
    updatePageData(currentPage, currentPageData);
    currentPageIsValid = _.isEmpty(currentPageData.errors);

    formRef.current.scrollIntoView();
    if (currentPageIsValid) {
      setSubmissionError(false);
      setSubmissionErrorChildren(null);

      if (pages[currentPage] && pages[currentPage].onNext) {
        let nextArgs = { pageData: pageData[currentPage]?.values, trigger: "nextButton", ...args }
        pages[currentPage].onNext(nextArgs);
      }

      lastPage ? handleSubmit() : setCurrentPage(currentPage + 1);
    } else {
      let error = "There was an issue submitting your information. Please check all fields and resubmit.";
      _satellite.track("form-error", { errorMsg: error });
      setSubmissionError(error);
    }
  }

  const handleSubmit = () => {
    setIsSubmitting(true);

    let formData = {};
    pages.forEach(page => {
      page.fields.filter(field => field.hidden).forEach(field => {
        Object.assign(formData, { [field.name]: field.value })
      })
    });

    const pageValues = pageData.map(page => {
      return Object.assign(formData, page.values);
    });

    const requestOptions = {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    };

    fetch(action, requestOptions).then(
      response => {
        setIsSubmitting(false);

        if (response.status == 200) {
          setSubmissionError(null);
        } else {
          let error = "There was an issue contacting the server; please try again.";
          _satellite.track("form-error", { errorMsg: error });
          setSubmissionError(error);
        }
        return response.json();
      }
    ).then(data => {
      if (!data.error) {
        if (uid === "quoting-widget") {
          data.result.estimateValues && setEstimateValues(data.result.estimateValues);
          data.result["form_submit"] && setFormData(data.result["form_submit"]["data_json"]);
        }
        setSubmissionSuccess(true);
      }
    }).catch(
      error => {
        _satellite.track("form-error", { errorMsg: error });
        setIsSubmitting(false);
        setSubmissionError(error);
      }
    );
  }

  // Success messaging component
  if (submissionSuccess && successComponent) {
    return React.createElement(successComponent, { ...successComponentProps, formData: formData, estimateValues: estimateValues });
  }

  const renderField = (pageIndex, field, fieldIndex) => {
    const values = pageData[pageIndex].values;
    const touched = pageData[pageIndex].touched;
    const errors = pageData[pageIndex].errors;
    const Component = FieldMap[field.inputType];
    const helperText = field.helperText || (field.required ? "Required" : null);
    const modalContent = field.infoModalContent;
    const valid = !errors.hasOwnProperty(field.name) && touched[field.name];
    const error = errors[field.name];

    if (["radio", "imageRadio"].includes(field.inputType)) {
      const additionalField = field.additionalCheckbox;

      return (
        <Component
          key={`field-${fieldIndex}`}
          label={field.label}
          helperText={helperText}
          helperTextColor={field.helperTextColor}
          infoFunction={modalContent ? () => openFormModal(modalContent) : null}
          name={field.name}
          themeColor={themeColor}
          onChange={(event) => updateValues(event, field, field.name)}
          additionalChange={(event) => updateValues(event, additionalField, additionalField.name, event.target.checked)}
          options={field.options}
          imageRadio={field.inputType == "imageRadio"}
          required={field.required}
          touched={touched[field.name] || (submissionError && !values[field.name])}
          valid={valid}
          error={error}
          {...field}
        />
      )
    } else if (field.inputType == "checkbox") {
      return (
        <Component
          key={`field-${fieldIndex}`}
          label={field.label}
          helperText={helperText}
          infoFunction={modalContent ? () => openFormModal(modalContent) : null}
          name={field.name}
          themeColor={themeColor}
          onChange={updateValues}
          options={field.options}
          required={field.required}
          touched={touched[field.name] || (submissionError && !values[field.name])}
          valid={valid}
          error={error}
          {...field}
        />
      )
    } else if (field.inputType == "select") {
      if (pageIsCurrent && !pageData[currentPage].values[field.name]) {
        let initialValue = (typeof field.options[0] == "string") ? field.options[0] : field.options[0].value;
        pageData[currentPage].values[field.name] = initialValue;
      }

      return (
        <Fieldset key={`field-${fieldIndex}`} margin={"0 0 40px"}>
          <Component
            label={field.label}
            infoFunction={modalContent ? () => openFormModal(modalContent) : null}
            name={field.name}
            themeColor={themeColor}
            options={field.options}
            onChange={(event) => updateValues(event, field)}
            required={field.required}
            touched={touched[field.name] || (submissionError && !values[field.name])}
            valid={valid}
            error={error}
            {...field}
          />
        </Fieldset>
      )
    } else {
      return (
        <Fieldset key={`field-${fieldIndex}`} margin={field.hidden ? "0" : "0 0 32px"}>
          <Component
            label={field.label}
            helperText={helperText}
            helperTextColor={field.helperTextColor}
            infoFunction={modalContent ? () => openFormModal(modalContent) : null}
            name={field.name}
            themeColor={themeColor}
            multiline={field.inputType == "textarea"}
            onBlur={(event) => updateTouched(event, field)}
            onChange={(event) => updateValues(event, field, field.name)}
            required={field.required}
            touched={touched[field.name] || (submissionError && !values[field.name])}
            valid={valid}
            error={error}
            defaultValue={field.defaultValue}
            {...field}
          />
        </Fieldset>
      )
    }
  };

  initializePageData(pages);

  // Form components
  return (
    <>
      <Form action={action} id={uid} method={method} ref={formRef} showStartPage={showStartPage}>
        {(pageCount > 1) && <FormProgress pages={pages} currentPage={currentPage} marginBottom={"32px"} fullPage={(uid) === "quoting-widget" ? true : false} />}

        {pages.map((page, pageIndex) => {
          const pageIsCurrent = pageIndex == currentPage;

          return (
            <FormPage
              active={pageIndex === currentPage}
              key={`page-${pageIndex}`}
              data={page.data}
              optionalStep={page.optionalStep}
              skipFunction={nextOrSubmit}
              hideBorder={page.hideBorder}
              title={page.title}
              titlePinkText={page.titlePinkText}
              description={page.description}
              cta={page.cta}
              openFormModal={openFormModal}
              error={submissionError}
              errorLabel={(uid == "quoting-widget") ? "Error Submitting Form:" : null}
              errorChildren={submissionErrorChildren}
              themeColor={themeColor}
            >
              {!page.component && page.fields?.map((field, fieldIndex) => {
                if (field.conditional && !conditionalFieldIsVisible(pageIndex, field)) {
                  return;
                }

                return renderField(pageIndex, field, fieldIndex);
              })}

              {page.component && React.createElement(page.component, {
                ...page.componentProps,
                blurValidation: updateTouched,
                changeValidation: updateValues,
                openFormModal: openFormModal,
                closeFormModal: closeFormModal,
                fields: page.fields,
                values: pageData[pageIndex].values,
                touched: pageData[pageIndex].touched,
                errors: pageData[pageIndex].errors,
                submissionError: submissionError,
                setSubmissionError: setSubmissionError,
                setSubmissionErrorChildren: setSubmissionErrorChildren,
                themeColor: themeColor,
                toggleBlockedForSubmission: toggleBlockedForSubmission,
                formProps: formProps
              })}
            </FormPage>
          )
        })}

        <FormFooter>
          <div className="buttons-container">
            {currentPage > 0 &&
              <FormButton
                color="#FFFFFF"
                data-track="prev-step"
                disabled={isBlockedForSubmission}
                onClick={(event) => prevPage(event)}
              >
                Previous
              </FormButton>
            }

            <FormButton
              color={themeColor}
              data-track={lastPage ? "form-submit" : "next-step"}
              disabled={isBlockedForSubmission}
              onClick={(event) => nextOrSubmit(event)}
            >
              {nextText}
            </FormButton>
          </div>
          {(uid !== "quoting-widget") &&
            <div className="form-branding">
              <p>
                Technology Powered by Owens Corning
              </p>
              <img src="https://imagecdn.owenscorning.com/ocimages/image/upload/v1575575892/meganav/oc-logo.svg" alt="Owens Corning Logo"/>
            </div>
          }

          {disclaimers &&
            <Disclaimers>
              {disclaimers.map((disclaimer) => (
                <Note>{disclaimer}</Note>
              ))}
            </Disclaimers>
          }
        </FormFooter>
      </Form>

      {formModalContent &&
        <FormModal
          data={{ "data-am-region": "Quoting Form Overlay" }}
          name="Quoting Form Modal"
          onClose={() => closeFormModal()}
          open={true}
        >
          {formModalContent}
        </FormModal>
      }
    </>
  );
};

export default FormWrapper;

FormWrapper.propTypes = {
  /* HTML: Data endpoint URL string for the form action attribute */
  action: PropTypes.string,
  /* HTML: Form submission method attribute – defaults to POST */
  method: PropTypes.string,
  /* JSX: Array of page data, including title, description, and fields */
  pages: PropTypes.arrayOf(PropTypes.object),
  /* HTML: String value to assign as form ID */
  uid: PropTypes.string,
  /* CSS: Hex value for color theme */
  themeColor: PropTypes.string
};
